import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  phone_verified: boolean | null;
  avatar_url: string | null;
  created_at: string;
}

interface UserWithProfile {
  id: string;
  email: string;
  created_at: string;
  profile: UserProfile | null;
  roles: string[];
}

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithProfile | null;
  onUserDeleted: () => void;
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  user,
  onUserDeleted,
}: DeleteUserDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const getFullName = (profile: UserProfile | null): string => {
    if (!profile) return "Unknown User";
    const firstName = profile.first_name || "";
    const lastName = profile.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Unknown User";
  };

  const handleDelete = async () => {
    if (!user) return;

    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId: user.id },
      });

      if (error) {
        console.error('Error deleting user:', error);
        
        // Check if it's a network/deployment issue
        if (error.message?.includes('Failed to fetch') || error.message?.includes('FunctionsFetchError')) {
          toast.error('Edge function not available', {
            description: 'The delete function may not be deployed yet. Please wait a moment and try again.',
          });
        } else {
          toast.error('Failed to delete user', {
            description: error.message || 'An unexpected error occurred',
          });
        }
        return;
      }

      if (data?.error) {
        console.error('Error from edge function:', data.error);
        toast.error('Failed to delete user', {
          description: data.error,
        });
        return;
      }

      toast.success('User deleted successfully');
      onUserDeleted();
    } catch (error) {
      console.error('Unexpected error deleting user:', error);
      toast.error('Failed to delete user', {
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <div>
              This action cannot be undone. This will permanently delete{" "}
              <span className="font-semibold text-foreground">
                {getFullName(user.profile)}
              </span>
              's account and remove all associated data.
            </div>
            {user.profile?.phone && (
              <div className="text-sm text-muted-foreground">
                Phone: {user.profile.phone}
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete User
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
